import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'

import {
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_WHITE
} from '../../constants/uiColors'

const actionButtonSx = {
  height: 40,
  px: 2.5,
  borderRadius: 1,
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  boxShadow: 'none'
}

/**
 * Base reutilizable para formularios presentados en un dialogo.
 * El contenido del formulario queda a cargo del consumidor; el encabezado,
 * acciones, envio y comportamiento durante la carga son consistentes.
 */
export default function FormDialog({
  open,
  title,
  subtitle,
  children,
  cancelLabel = 'Cancelar',
  submitLabel = 'Guardar',
  loading = false,
  onClose,
  onSubmit,
  onExited,
  maxWidth = 'md',
  paperSx = {},
  headerBoxSx = {},
  titleSx = {},
  subtitleSx = {},
  contentSx = {},
  actionsSx = {},
  cancelButtonSx = {},
  submitButtonSx = {}
}) {
  const generatedId = React.useId().replace(/:/g, '')
  const titleId = `form-dialog-title-${generatedId}`
  const formId = `form-dialog-form-${generatedId}`

  const handleClose = (_event, reason) => {
    if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return
    onClose?.()
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!loading) onSubmit?.(event)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onExited }}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          mx: { xs: 1.5, sm: 3 },
          width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
          borderRadius: 1,
          backgroundColor: UI_COLOR_WHITE,
          ...paperSx
        }
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: '24px' },
          pt: { xs: 2.5, sm: '26px' },
          pb: '10px',
          ...headerBoxSx
        }}
      >
        <DialogTitle
          id={titleId}
          sx={{
            p: 0,
            color: UI_COLOR_TEXT_HEADING,
            fontSize: { xs: 22, sm: 24 },
            fontWeight: 700,
            lineHeight: 1.2,
            ...titleSx
          }}
        >
          {title}
        </DialogTitle>
        {subtitle ? (
          <Typography
            sx={{
              mt: 0.75,
              color: UI_COLOR_TEXT_SUBTITLE,
              fontSize: { xs: 14, sm: 16 },
              ...subtitleSx
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <DialogContent
        dividers
        sx={{ px: { xs: 2.5, sm: '24px' }, py: { xs: 2.5, sm: '28px' }, ...contentSx }}
      >
        <Box id={formId} component="form" noValidate onSubmit={handleSubmit}>
          {children}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          gap: 1.25,
          px: { xs: 2.5, sm: '24px' },
          py: '20px',
          borderTop: `1px solid ${UI_COLOR_BORDER_MEDIUM}`,
          ...actionsSx
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            ...actionButtonSx,
            color: UI_COLOR_TEXT_SUBTITLE,
            borderColor: UI_COLOR_TEXT_SUBTITLE,
            '&:hover': { borderColor: UI_COLOR_TEXT_HEADING, backgroundColor: UI_COLOR_WHITE },
            ...cancelButtonSx
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          disableElevation
          disabled={loading}
          sx={{
            ...actionButtonSx,
            backgroundColor: UI_COLOR_PRIMARY_ALT,
            '&:hover': { backgroundColor: UI_COLOR_PRIMARY_HOVER, boxShadow: 'none' },
            ...submitButtonSx
          }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
